import { useEffect, useState } from 'react';
import { useTestContext } from '../../../context/testContext';
import { useAppointment } from '../../../context/appointmentContext';
import { client } from '../../../helpers/api-client';
import { countIshiharaResults, formatIshiharaAnswer } from '../../../helpers/ishihara';
import { DataTable, SessionActions, StatusPill, useSessionUI, useSessionChrome } from '../../../ui';
import { StepBody, Heading } from '../testScreenKit';

const RESULT_TONE = { Correct: 'success', Incorrect: 'error' };

/** Rows for the results table from stored result_data ({ plateId: { plate, result, selected } }). */
export const colourVisionRows = (answers) =>
  Object.entries(answers || {})
    .filter(([, a]) => a && typeof a === 'object' && 'result' in a)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([id, a]) => ({
      id,
      plate: id === '01' ? `${id} (practice)` : id,
      selected: formatIshiharaAnswer(a.selected),
      expected: a.plate?.answer ? formatIshiharaAnswer(a.plate.answer) : '—',
      result: a.result,
    }));

// Colour Vision results: one row per plate + "N/M plates correct"; Save goes through confirm().
// Without localResults it shows a saved result (Completed Examinations -> View Results).
const ColourVisionResults = ({ localResults, setActiveTab, onRestart }) => {
  const [remote, setRemote] = useState(null);
  const [saving, setSaving] = useState(false);
  const { appointment } = useAppointment();
  const { confirm, toast } = useSessionUI();
  const { setTitle } = useSessionChrome();
  const { testData, updateTestData } = useTestContext();
  const answers = localResults || remote || {};
  const rows = colourVisionRows(answers);
  const { correct } = countIshiharaResults(answers);
  const scored = rows.filter((r) => r.id !== '01').length;

  useEffect(() => {
    if (!localResults) setTitle('Colour Vision Test Results');
  }, [localResults, setTitle]);

  useEffect(() => {
    if (localResults || !testData?.testResultsId) return;
    client(`api/appointment-test-result/${testData.testResultsId}`).then(
      ({ result_data }) => setRemote(result_data ? JSON.parse(result_data) : {}),
      (error) => console.error(error),
    );
  }, [localResults, testData?.testResultsId]);

  const save = async () => {
    if (!(await confirm({ message: 'Are you sure you want to save the test results?' }))) return;
    setSaving(true);
    client(`api/appointment-test-result/`, {
      body: { appointment_eye_test: testData.eyeTestId, appointment: appointment.currentAppointment.id, result_data: JSON.stringify(localResults) },
    }).then(
      () => {
        setSaving(false);
        updateTestData({ testInProgress: false });
        toast({ tone: 'success', title: 'Results saved', body: 'Colour Vision results were added to this appointment.' });
        setActiveTab('results');
      },
      (error) => {
        setSaving(false);
        console.error('Unable to save results', error);
        toast({ tone: 'error', title: 'Could not save the results', body: 'Please try again.' });
      },
    );
  };

  return (
    <StepBody gap={16} data-testid='colour-vision-results'>
      <Heading>{`${correct}/${scored} plates correct`}</Heading>
      <div style={{ flex: 1, minHeight: 0, width: '100%', maxWidth: 640, alignSelf: 'center' }}>
        <DataTable
          maxHeight='100%'
          empty='No answers recorded.'
          rows={rows}
          columns={[
            { key: 'plate', label: 'Plate' },
            { key: 'selected', label: 'Answer' },
            { key: 'expected', label: 'Expected' },
            { key: 'result', label: 'Result', render: (r) => (r.result ? <StatusPill tone={RESULT_TONE[r.result]}>{r.result}</StatusPill> : '—') },
          ]}
        />
      </div>
      <SessionActions
        onBack={() => (localResults ? onRestart?.() : setActiveTab('results'))}
        backLabel={localResults ? 'Restart' : 'Back'}
        primary={localResults ? { label: saving ? 'Saving…' : 'Save Test Results', onClick: save, disabled: saving || rows.length === 0 } : undefined}
      />
    </StepBody>
  );
};

export default ColourVisionResults;
